import React, { ReactNode, useCallback, useEffect } from 'react';

import { VehicleYmmGroup } from '../../../models/vehicle-ymm';
import FDSSegmentedControl from './segmentedControl';
import YMMSelector from './ymm-selector/ymm-selector';

import './smash-vehicle-selector-fds.scss';
import {
    AuthenticationState,
    useAuthentication,
} from '../../../hooks/use-authentication';
import { useHistory } from 'react-router-dom';
import { findByAlias } from '../../../routes';
import { useAnalytics } from '../../../hooks/use-analytics';
import VINSelector from '../vehicle-selector-fds/vin-selector/vin-selector';
import { RedirectData } from '../../../support/components/FMALogin/FMALogin';

export interface VehicleSelectorLabels {
    yearDropdown: string;
    modelDropdown: string;
    submit: string;
    header: string;
    vinPlaceholder: string;
    vinHeader: string;
    dividerText?: string;
    vehicleSelectorTitle?: string;
    vehicleSelectorDescription?: string;
    vehicleSelectorDescriptionWithDisclaimer?: ReactNode;
    vehicleSelectorDescAuthState?: string;
    segmentedControlVinButtonLabel?: string;
    segmentedControlYearAndModelButtonLabel?: string;
    whereDoIFindVinCta?: string;
    vehicleSelectorSubmitButtonLabel?: string;
}

export interface Props {
    labels: VehicleSelectorLabels;
    redirectData?: RedirectData;
    tooltip?: ReactNode;
    vehicleDropdownData?: VehicleYmmGroup[];
    articlesSearch?: boolean;
    ymmDefaultView?: boolean;
}

export const SmashVehicleSelectorFds = (props: Props) => {
    const [fireEvent] = useAnalytics();
    const { labels, articlesSearch } = props;
    const history = useHistory();
    const goToDashboard = useCallback(() => {
        const ymmPath = findByAlias('VehicleDashboardView');
        history.push(ymmPath);
    }, [history]);
    useEffect(() => {
        const fdsElement = document.querySelector(
            '.smash-vehicle-selector-fds'
        ) as HTMLElement;
        new FDSSegmentedControl(fdsElement);

        const myVehicleDashboardEle = document.querySelector(
            '.smash-dashboard-title'
        ) as HTMLElement;

        myVehicleDashboardEle
            ?.getElementsByTagName('a')[0]
            ?.addEventListener('click', e => {
                e.preventDefault();
                goToDashboard();
            });
    });
    useEffect(() => {
        const findMyVinLink = document.querySelector(
            '.find-vin-cta'
        ) as HTMLElement;

        findMyVinLink?.addEventListener('click', () => {
            fireEvent(
                'smash-home-help-find-vin-onclick-event',
                undefined,
                undefined,
                false
            );
        });
    }, []);
    const [isAuth, ,] = useAuthentication();
    const includeHeadlineMessage = () => {
        if (
            isAuth === AuthenticationState.Authenticated &&
            props.labels.vehicleSelectorDescAuthState
        ) {
            return (
                <span
                    className="smash-dashboard-title"
                    dangerouslySetInnerHTML={{
                        __html: props.labels.vehicleSelectorDescAuthState,
                    }}
                />
            );
        }
        return (
            props.labels.vehicleSelectorDescriptionWithDisclaimer ||
            props.labels.vehicleSelectorDescription
        );
    };

    return (
        <div className="smash-vehicle-selector-fds">
            {articlesSearch ? (
                <>
                    <h2 className="vehicle-selector-title">
                        {props.labels.vehicleSelectorTitle}
                    </h2>
                    <p className="vehicle-selector-description">
                        {props.labels.vehicleSelectorDescription}
                    </p>
                </>
            ) : (
                <div className="smash-vehicle-selector-title">
                    <span>{includeHeadlineMessage()}</span>
                </div>
            )}
            <div className="fds-segmented-control fds-color__bg--light">
                <div className="fds-segmented-control__button-list-flex-container">
                    <div
                        className="fds-segmented-control__button-list-wrapper"
                        aria-label=""
                    >
                        <div className="fds-segmented-control__button-list">
                            <button
                                className="fds-segmented-control__button"
                                aria-pressed={
                                    props.ymmDefaultView ? 'false' : 'true'
                                }
                                aria-controls="FDSSegmentedControl_2Buttons_NoIcon_Panel1"
                                id="FDSSegmentedControl_2Buttons_NoIcon_Button1"
                            >
                                <span className="fds-segmented-control__button-text">
                                    {
                                        props.labels
                                            .segmentedControlVinButtonLabel
                                    }
                                </span>
                            </button>
                            <button
                                className="fds-segmented-control__button"
                                aria-pressed={
                                    props.ymmDefaultView ? 'true' : 'false'
                                }
                                aria-controls="FDSSegmentedControl_2Buttons_NoIcon_Panel2"
                                id="FDSSegmentedControl_2Buttons_NoIcon_Button2"
                            >
                                <span className="fds-segmented-control__button-text">
                                    {
                                        props.labels
                                            .segmentedControlYearAndModelButtonLabel
                                    }
                                </span>
                            </button>
                        </div>
                        <span className="fds-segmented-control__slider">
                            <span className="fds-segmented-control__slider-text">
                                {props.labels.segmentedControlVinButtonLabel}
                            </span>
                        </span>
                    </div>
                </div>
                <div className="fds-segmented-control__panels">
                    <div
                        id="FDSSegmentedControl_2Buttons_NoIcon_Panel1"
                        className="fds-segmented-control__panel"
                        aria-labelledby="FDSSegmentedControl_2Buttons_NoIcon_Button1"
                        tabIndex={0}
                    >
                        <div className="fds-segmented-control__panel-content">
                            <VINSelector
                                page="SMASH"
                                vinHeaderLabel={''}
                                whereDoIFindVinCta={labels.whereDoIFindVinCta}
                                redirectData={props.redirectData}
                                vinPlaceholderLabel={labels.vinPlaceholder}
                                submitLabel={labels.submit}
                                tooltip={props.tooltip}
                                eventName={
                                    articlesSearch
                                        ? 'owner-dyf-landing-page-cta'
                                        : 'smash-home-search-cta-onclick-event'
                                }
                            />
                        </div>
                    </div>
                    <div
                        role="group"
                        id="FDSSegmentedControl_2Buttons_NoIcon_Panel2"
                        className="fds-segmented-control__panel"
                        aria-labelledby="FDSSegmentedControl_2Buttons_NoIcon_Button2"
                        tabIndex={0}
                    >
                        <div className="fds-segmented-control__panel-content">
                            <YMMSelector
                                {...props}
                                ymmHeaderLabel=""
                                yearDropdownLabel={labels.yearDropdown}
                                modelDropdownLabel={labels.modelDropdown}
                                submitLabel={labels.submit}
                                analyticsEventName={
                                    articlesSearch
                                        ? 'owner-dyf-landing-page-cta'
                                        : 'smash-home-search-cta-onclick-event'
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
